import { HeartPulse } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "../../../shared/navegacao/navegacao";

/**
 * Casca das telas de acesso (login, esqueci a senha, definir senha).
 *
 * Estrutura do bloco `login-02` do shadcn (duas colunas, painel some em tela
 * estreita): painel da marca na noite da MONTE (ink com raios royal) a esquerda
 * e formulario a direita.
 */
export function LayoutDeAcesso({ children }: { readonly children: ReactNode }) {
  return (
    <div className="grid min-h-svh bg-page-background lg:h-svh lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] lg:overflow-hidden">
      <PainelDaMarca />
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex justify-center lg:hidden">
          <Marca className="text-foreground" />
        </div>
        <main className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-sm">{children}</div>
        </main>
        <p className="text-right text-xs text-muted-foreground">
          Precisa de ajuda? Fale com o seu consultor da Monte Saúde.
        </p>
      </div>
    </div>
  );
}

/** Cabecalho padrao do formulario: rotulo laranja, titulo leve e descricao. */
export function CabecalhoDeAcesso({
  rotulo = "Área do cliente",
  titulo,
  descricao,
}: {
  readonly rotulo?: string;
  readonly titulo: string;
  readonly descricao: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-[10.5px] font-extrabold tracking-[0.14em] text-primary uppercase">
        {rotulo}
      </p>
      <h1 className="text-4xl leading-[1.05] font-medium tracking-[-0.04em] text-foreground">
        {titulo}
      </h1>
      <p className="text-[13px] leading-relaxed text-muted-foreground">{descricao}</p>
    </div>
  );
}

/** Classes dos campos e do botao principal no padrao pilula da tela de acesso. */
export const CAMPO_DE_ACESSO = "h-13 rounded-full bg-card px-5 text-sm shadow-none";
export const BOTAO_DE_ACESSO = "h-13 w-full rounded-full text-sm font-bold";

function Marca({ className }: { readonly className?: string }) {
  return (
    <Link para="/entrar" className={`flex items-center gap-2 ${className ?? ""}`}>
      <span className="flex size-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
        <HeartPulse className="size-4" aria-hidden="true" />
      </span>
      <span className="text-[22px] font-bold tracking-[-0.04em]">monte saúde</span>
    </Link>
  );
}

/** Etapas do metodo da Monte, na ordem em que o painel acompanha o cliente. */
const ETAPAS = ["Anúncios", "WhatsApp", "Agendamentos", "Indicadores"] as const;

function PainelDaMarca() {
  return (
    <aside className="fundo-de-raios hidden overflow-hidden px-12 py-10 text-white lg:flex lg:flex-col">
      <div className="flex items-center justify-between">
        <Marca />
        <span className="rounded-full border border-white/65 px-3 py-1.5 text-[11px] font-bold tracking-[0.12em] uppercase">
          Área do cliente
        </span>
      </div>

      <div className="my-auto flex max-w-xl flex-col gap-6">
        <p className="text-[10.5px] font-bold tracking-[0.16em] text-ice uppercase">
          Painel do cliente Monte Saúde
        </p>
        <p className="text-[clamp(2.75rem,4.4vw,3.75rem)] leading-[0.98] font-semibold tracking-[-0.045em]">
          Do anúncio ao agendamento, tudo à vista.
        </p>
        <p className="max-w-lg text-[17px] leading-relaxed text-white/75">
          Acompanhe as oportunidades da sua clínica, os indicadores de marketing e vendas e a
          evolução do projeto com a Monte, em tempo real.
        </p>
      </div>

      <ol aria-label="Como o painel acompanha a sua clínica" className="flex flex-wrap items-center gap-2 text-xs font-medium">
        {ETAPAS.map((etapa, indice) => (
          <li key={etapa} className="flex items-center gap-2">
            <span className="rounded-full border border-white/25 bg-secondary/40 px-3 py-1.5 backdrop-blur-sm">
              {etapa}
            </span>
            {indice < ETAPAS.length - 1 ? (
              <span aria-hidden="true" className="text-ice">
                →
              </span>
            ) : null}
          </li>
        ))}
      </ol>
    </aside>
  );
}
